import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { Link } from 'react-router-dom';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Tooltip } from '@mui/material';

import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';
import { CenteredCircularProgress } from 'components/Progress';
import InitialAvatar from 'components/Avatar/InitialAvatar';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';
import CustomDatePicker from 'components/SharedFormComponents/CustomDatePicker';
import TaskStatusChip from 'containers/TaskDetails/components/TaskStatusChip';
import { EmptyTaskIcon } from 'components/Icons/EmptyTaskIcon';

import AuditQuestionActivities from 'stores/AuditQuestionActivities';

import API from 'utils/api';
import Logger from 'utils/logger';
import { UuidableName } from 'utils/api/types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { AuditLinkEntity, QuestionTaskLinkTask } from 'utils/api/modules/entities/entities.types';
import { useStore } from 'utils/hooks/store';
import { formatDueDate } from 'utils/date';
import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import routes from 'core/routes';

import { EmptyState, EmptyTabState, InlineError, RetryRow, ScrollableContainer, TabBody } from '../styles';
import {
  AddRow,
  CreateActions,
  CreateCard,
  CreateField,
  CreateFields,
  DueDateValue,
  MetaField,
  MetaLabel,
  MetaValue,
  MetaValueText,
  MutedValue,
  TaskCard,
  TaskCardHeader,
  TaskCardName,
  TaskMetaRow,
} from './styles';

type CreateValues = {
  name: string;
  assigned: UuidableName | null;
  dueDate: string | null;
};

const INITIAL_VALUES: CreateValues = { name: '', assigned: null, dueDate: null };

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Please enter a task name.'),
  assigned: Yup.mixed().nullable(),
  dueDate: Yup.string().nullable(),
});

const TaskCardRow = observer(({ task }: { task: QuestionTaskLinkTask }): React.ReactElement => {
  const to = reverse(routes.dashboard.tasks.details, { taskId: task.uuid });
  const due = task.dueDate ? formatDueDate(task.dueDate) : undefined;

  return (
    <TaskCard data-testid="audit-question-action-row">
      <TaskCardHeader>
        <TaskCardName to={to}>{task.name}</TaskCardName>
        <Tooltip title="Open action" placement="top">
          <IconButton component={Link} to={to} size="small" aria-label={`Open ${task.name}`}>
            <OpenInNewIcon sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Tooltip>
      </TaskCardHeader>
      <TaskMetaRow>
        <MetaField>
          <MetaLabel>Assignee</MetaLabel>
          <MetaValue>
            {task.assigned ? (
              <>
                <InitialAvatar name={task.assigned.name} size="small" />
                <MetaValueText>{task.assigned.name}</MetaValueText>
              </>
            ) : (
              <MutedValue>Unassigned</MutedValue>
            )}
          </MetaValue>
        </MetaField>
        <MetaField>
          <MetaLabel>Due date</MetaLabel>
          <MetaValue>
            {due ? <DueDateValue $color={due.color}>{due.name}</DueDateValue> : <MutedValue>No due date</MutedValue>}
          </MetaValue>
        </MetaField>
        <MetaField>
          <MetaLabel>Status</MetaLabel>
          <MetaValue>
            <TaskStatusChip status={task.status} size="small" />
          </MetaValue>
        </MetaField>
      </TaskMetaRow>
    </TaskCard>
  );
});

/** The question's tasks, off the same rows the badge counts; `entityUuid` makes the tab writable. */
const ActionsTab = ({
  activities,
  questionKey,
  entityUuid,
}: {
  activities: AuditQuestionActivities;
  questionKey: string;
  entityUuid?: string;
}): React.ReactElement => {
  const { notification } = useStore();
  const [adding, setAdding] = React.useState(false);

  // `tasksFailed` is terminal: without it a rejected load refires this effect until the drawer closes.
  React.useEffect(() => {
    activities.ensureTasks(questionKey);
  }, [activities, questionKey, activities.tasks, activities.tasksLoading, activities.tasksFailed]);

  const onCreate = async (values: CreateValues, helpers: FormikHelpers<CreateValues>): Promise<void> => {
    if (!entityUuid) {
      return;
    }

    try {
      // Assignee and due date cannot be PATCHed onto a task that does not exist yet, so they ride
      // along in the create call.
      const { data } = await API.tasks.createTaskForForm({
        name: values.name.trim(),
        assigned: values.assigned?.uuid,
        dueDate: values.dueDate ?? undefined,
        links: [{ entity: AuditLinkEntity, instance: entityUuid, extraData: { questionId: questionKey } }],
      })();
      helpers.resetForm();
      setAdding(false);
      // The Activity tab shows this link too, but don't fetch a feed nobody opened.
      await Promise.all([
        activities.loadTasks(),
        activities.loadStats(),
        ...(activities.activities === undefined ? [] : [activities.refreshFeed()]),
      ]);
      // The record header never remounts on this flow, so its count would otherwise go stale.
      postMessage(EventType.CreatedTask, data);
      notification.enqueueSuccessSnackbar('Task created successfully');
    } catch (e) {
      notification.enqueueErrorSnackbar('The task could not be created. Something went wrong.');
      Logger.error('Failed to create an audit question action', e);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  if (activities.tasksFailed) {
    return (
      <TabBody>
        <EmptyState>Tasks could not be loaded.</EmptyState>
        <RetryRow>
          <Button variant="outlined" size="small" onClick={activities.retryTasks}>
            Try again
          </Button>
        </RetryRow>
      </TabBody>
    );
  }

  if (activities.tasks === undefined) {
    return (
      <TabBody>
        <CenteredCircularProgress />
      </TabBody>
    );
  }

  const tasks = activities.tasks;

  return (
    <TabBody>
      <ScrollableContainer>
        {tasks.length === 0 && !adding ? (
          <EmptyTabState>
            <EmptyTaskIcon />
            <p>No tasks yet.</p>
            <p>Add a task to track any follow-up.</p>
          </EmptyTabState>
        ) : (
          tasks.map((task) => <TaskCardRow key={task.uuid} task={task} />)
        )}

        {adding && (
          <Formik initialValues={INITIAL_VALUES} validationSchema={validationSchema} onSubmit={onCreate}>
            {({ values, errors, touched, isSubmitting, submitCount, setFieldValue, setFieldTouched, resetForm }) => {
              const nameInvalid = (touched.name || submitCount > 0) && Boolean(errors.name);
              const cancel = (): void => {
                resetForm();
                setAdding(false);
              };

              return (
                <Form>
                  <CreateCard data-testid="audit-question-action-create">
                    <CreateField>
                      <MetaLabel>Task name</MetaLabel>
                      <StyledTextField
                        autoFocus
                        fullWidth
                        required
                        size="small"
                        placeholder="Describe the task"
                        value={values.name}
                        onChange={(event) => setFieldValue('name', event.target.value)}
                        onBlur={() => setFieldTouched('name', true)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            cancel();
                          }
                        }}
                        error={nameInvalid}
                        inputProps={{ 'data-testid': 'audit-question-action-name' }}
                      />
                      {/* Not `helperText`: the input styles' white background would wrap the message too. */}
                      {nameInvalid && <InlineError role="alert">{errors.name}</InlineError>}
                    </CreateField>
                    <CreateFields>
                      <CreateField>
                        <MetaLabel>Assignee</MetaLabel>
                        <CustomUserDropdownField
                          value={values.assigned}
                          onChange={(assigned) => setFieldValue('assigned', assigned)}
                          description=""
                          size="small"
                        />
                      </CreateField>
                      <CreateField>
                        <MetaLabel>Due date</MetaLabel>
                        <CustomDatePicker
                          tzFree
                          value={values.dueDate ?? undefined}
                          onChange={(value: AnswerTypes) => setFieldValue('dueDate', value || null)}
                          description=""
                          size="small"
                        />
                      </CreateField>
                    </CreateFields>
                    <CreateActions>
                      <Button variant="text" size="small" onClick={cancel} data-testid="audit-question-action-cancel">
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        type="submit"
                        disabled={isSubmitting || !values.name.trim()}
                        data-testid="audit-question-action-submit"
                      >
                        Create task
                      </Button>
                    </CreateActions>
                  </CreateCard>
                </Form>
              );
            }}
          </Formik>
        )}
      </ScrollableContainer>

      {entityUuid && !adding && (
        <AddRow>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
            data-testid="audit-question-action-add"
            sx={{ width: '50%' }}
          >
            Add new task
          </Button>
        </AddRow>
      )}
    </TabBody>
  );
};

export default observer(ActionsTab);
